// Home page copy, from sales & marketing's homepage direction
// (docs/claude-artifact-examples/xeratec-homepage-mockup.html).

export const hero = {
  eyebrow: "Construction Technology · Hancock, Michigan · Second-Generation",
  title: "Stop fighting software that was never *built for you.*",
  text: "Xeratec gets rid of the headaches that keep owners chained to the office — the double entry, the lost tickets, the systems that don't talk. So the work runs without you standing over it. So you can actually take the vacation.",
  secondary: "See what this looks like for you",
  // Plays muted behind the hero. The live site's YouTube hero video
  // (youtu.be/A0QTQsukz_A), saved as a file so YouTube's player overlay
  // never shows over the hero.
  video: "assets/hero-loop.mp4",
};

export const selector = {
  eyebrow: "Make this page about you",
  title: "What do you run?",
  text: "Pick your world and your size. We'll show you the headaches we usually find — and what happened when a company like yours handed them to us.",
  tradeLabel: "Your world",
  sizeLabel: "Your size",
};

const cases = {
  norco: "/norco-transport-driving-efficiency-across-every-load/",
  burns: "/burns-dirt-bidding-dirt-moving-smarter/",
  ljj: "/ljj-construction-from-the-jobsite-to-quickbooks/",
  jrt: "/jrt-mechanical-streamlining-operations-with-custom-project-management-software/",
  mjSafety:
    "/mj-electric-transforming-saftey-with-digital-assessment-solutions/",
};

/**
 * "What do you run?" answers. `story` (optional) is the case study shown
 * beside the headaches (its `quote`, `name` and `role` are optional); without one, only the headaches show. `results`
 * (optional) is the Results page filter "See more companies like yours" opens.
 */
export const trades = [
  {
    id: "dirt",
    label: "Dirt & Excavation",
    pains: [
      "Bids live in one guy's spreadsheet — and he's on vacation",
      "Load tickets ride around in trucks for a week before billing ever sees them",
      "The office re-types what the field already wrote down",
    ],
    relief:
      "We turn that into one clean flow: bid it, run it, ticket it, bill it — without the double entry.",
    story: {
      title: "Burns Dirt: bidding dirt moving smarter",
      body: "Burns Dirt was winning work but bleeding hours on bids. We rebuilt how estimates come together — faster takeoffs, fewer surprises, and numbers the whole team can trust.",
      href: cases.burns,
      quote:
        "We had a solid prototype in Sheets — but it kept breaking, and we hit a wall. Xeratec turned our rough concept into a clean, reliable tool anyone on our team can use.",
      name: "Nic Parish",
      role: "CEO, Burns Dirt Construction",
    },
    results: "dirt",
  },
  {
    id: "trucking",
    label: "Trucking & Hauling",
    pains: [
      "Dispatch runs on group texts and a whiteboard",
      "Load tickets get lost somewhere between the cab and the invoice",
      "You find out a truck sat idle three days after it happened",
    ],
    relief:
      "We put dispatch, tickets, and billing in one place — so every load gets invoiced and nothing sits.",
    story: {
      title: "Norco Transport: efficiency across every load",
      body: "Norco needed every load to count. We built the systems that track work from dispatch to invoice, so revenue stops leaking through the cracks in the paperwork.",
      href: cases.norco,
      quote:
        "Previously, each ticket was handled three separate times — creating three different opportunities for errors. With our new software, the system flags errors automatically.",
      name: "Eddie Leos",
      role: "CEO, Norco Transport",
    },
    results: "trucking",
  },
  {
    id: "concrete",
    label: "Concrete & Sitework",
    pains: [
      "Job costing happens at tax time, not while the job is running",
      "Time cards come in crumpled on Monday morning",
      "Change orders live in somebody's voicemail",
    ],
    relief:
      "We get job costs, hours, and change orders flowing in daily — so you know where a job stands while you can still do something about it.",
    story: {
      title: "Burns Dirt: bidding dirt moving smarter",
      body: "For a sitework contractor like Burns Dirt, the fix started with the numbers: cleaner bids, live job data, and a team that finally trusts what the reports say.",
      href: cases.burns,
      quote:
        "BattleShip took our 'Moneyball' strategy from idea to execution — it's now baked into how we operate.",
      name: "Nic Parish",
      role: "CEO, Burns Dirt Construction",
    },
    results: "dirt",
  },
  {
    id: "gc",
    label: "General Contractor",
    pains: [
      "You bought five apps and none of them talk to each other",
      "The field hates the software the office picked",
      "Reports take a week to build and are wrong by Friday",
    ],
    relief:
      "We connect what you own, cut what you don't need, and build the missing pieces — so the field and office finally see the same job.",
    story: {
      title: "LJJ Construction: from the jobsite to QuickBooks, entered once",
      body: "LJJ's crew hours started on paper timecards and got typed into QuickBooks a second time for payroll. We built one platform that connects the jobsite to the books: hours entered once in the field, approved by the project manager, and synced to QuickBooks without being retyped.",
      href: cases.ljj,
    },
    results: "mechanical",
  },
  {
    id: "mechanical",
    label: "Mechanical",
    pains: [
      "You're paying for a pile of software subscriptions and using a third of it",
      "RFIs, change orders and daily reports each live in a different app",
      "Every department works in its own silo",
    ],
    relief:
      "We replace the pile with one system built around how your shop runs — so work hands off between teams instead of getting lost between apps.",
    story: {
      title: "JRT Mechanical: one platform instead of ten",
      body: "JRT was spending more than $140,000 a year on 10+ tools that didn't talk. We built one platform for RFIs, change orders, daily reports, time and tasks — used by every department.",
      href: cases.jrt,
      quote:
        "We're now able to do more with less, and the efficiency gains have been incredible.",
      name: "Carl Tapani",
      role: "President, JRT Mechanical Inc.",
    },
    results: "mechanical",
  },
  {
    id: "electrical",
    label: "Electrical",
    pains: [
      "Safety checklists are on paper and different at every site",
      "Equipment lists get rebuilt by hand for every job",
      "Crews spend their time gathering data instead of acting on it",
    ],
    relief:
      "We put checklists, equipment and reporting into simple field apps — one standard everywhere, and data the office can act on the same day.",
    story: {
      title: "MJ Electric: safety on one standard",
      body: "MJ Electric's safety assessments varied from wind farm to substation. We built a digital safety platform that keeps every site to one standard and turns assessments into live trend data.",
      href: cases.mjSafety,
      quote:
        "Xeratec has responded rapidly when needed to maintain and develop our technical solutions and keep our systems relevant to the changing needs of our business.",
      name: "Cliff Cook",
      role: "Director of I.T., MJ Electric",
    },
    results: "electrical",
  },
  {
    id: "plumbing",
    label: "Plumbing",
    pains: [
      "Service calls get scheduled on a whiteboard and a group text",
      "Techs fill out paper tickets that sit in the truck all week",
      "Nobody knows which jobs actually made money",
    ],
    relief:
      "We get scheduling, tickets and job costs into one place — entered once, in the field, and ready for billing the same day.",
    results: "mechanical",
  },
];

export const sizes = [
  {
    id: "s",
    label: "Me & a small crew (1–10)",
    note: "You're the office AND the crew. You didn't start your business to do paperwork, and now every hour you're in the office is an hour off the machine.",
  },
  {
    id: "m",
    label: "Growing (11–50)",
    note: "You've outgrown spreadsheets, but the big-enterprise systems don't fit you either. That gap is exactly where we live.",
  },
  {
    id: "l",
    label: "Big operation (50+)",
    note: "You've got systems. The problem is they don't talk to each other — and nobody fully trusts the numbers.",
  },
];

export const process = {
  eyebrow: "How we work",
  title: "People. Process. *Then* tech.",
  text: 'Most "software problems" are process problems wearing a disguise. So we don\'t lead with software.',
  steps: [
    {
      title: "We start with your team",
      text: "If the crew won't use it, it doesn't matter what it does. We start on-site, with the people actually doing the work — not a demo deck.",
    },
    {
      title: "We straighten the process",
      text: "Before anything gets built, we map how work really moves through your shop and cut the steps that only exist out of habit.",
    },
    {
      title: "Then, the tech",
      text: "Sometimes it's a new system. Sometimes it's connecting what you already own. We build what you need and what you will use, nothing extra.",
    },
  ],
};

export const math = {
  eyebrow: "Let's talk about cost",
  title: "They were paying for a dozen tools. *Now they pay for one.*",
  text: "One of our customers was paying for more than a dozen software subscriptions — a six-figure pile, every year — and actually using maybe a third of the features. We replaced the whole stack with one platform built exactly for how they work.",
  pileLabel: "Before: the subscription pile",
  pile: [
    "Project tracking",
    "Time cards",
    "Daily reports",
    "RFIs",
    "Change orders",
    "Scheduling",
    "Forms",
    "File storage",
    "Task lists",
    "Meeting notes",
    "Spreadsheets",
    "…and the duct tape between them",
  ],
  oneLabel: "After: one system",
  oneTitle: "Everything they need. Nothing they don't.",
  oneText:
    "Built around their process, used by every department — for a fraction of what the pile was costing them.",
  bottom:
    "When one system replaces the stack of tools you're already paying for — plus the hours you burn making them talk to each other — it doesn't cost.",
  bottomStrong: "It pays.",
  link: "See the real story",
  href: "/jrt-mechanical-streamlining-operations-with-custom-project-management-software/",
};

export const logosLabel = "Trusted by builders, haulers and manufacturers";

export const partnersTitle =
  "Partnering with and guiding companies who positively impact society.";

/**
 * Client marks, from public/brand/clients/ in file-name order. To add one,
 * drop the logo (trimmed to the mark itself) in that folder, named as it
 * should read: "MJ-Electric.png" shows as "MJ Electric". Delete the file to
 * remove it.
 */
export { default as clientLogos } from "virtual:client-logos";

export const testimonials = [
  {
    quote:
      "“Engaging consultants can often be a challenge that leads to personal and professional anxiety. Xeratec was able to work with our team to alleviate those anxieties and developed a working relationship that helped us excel at our project. After starting with an onsite onboarding visit, the Xeratec team was able to establish a connection with our team and dig in to understand our processes and requirements. They delivered a quality product at a regular cadence that helped us achieve our goals and progress to the next stage of our business.”",
    name: "Jason Pucker",
    company: "Michels Corporation",
  },
  {
    quote:
      "“Xeratec continues to be a great partner, delivering a quality product in a cost effective and timely manner.”",
    name: "Matthew Kangas",
    company: "Somero Enterprises",
  },
  {
    quote:
      "“We partnered with Xeratec years ago to deliver technical development to improve our overall business functions. Our experience has been very positive. Xeratec has responded rapidly when needed to maintain and develop our technical solutions and keep our systems relevant to the changing needs of the business.”",
    name: "Cliff Cook",
    company: "Director of I.T.",
  },
  {
    quote:
      "“Xeratec has been the perfect partner for LoadTraxx. They were very flexible during the invention phase of the product. With their ability to be agile and their incredibly competent technical team we have a product that we can bring to the marketplace!”",
    name: "Russ Tapio",
    company: "Co-Founder, LoadTraxx",
  },
];

/** Closing statement at the foot of the live home page. */
export const closing =
  "We partner with construction leaders to design software and AI solutions that connect teams, simplify operations, and deliver measurable results.";
